import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { ListChecks, Plus, Trash2, ArrowUp, ArrowDown, Loader2, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { PRIO_STYLES, invalidatePrioConfig } from '../PrioCriteriaPicker';

const KINDS = [
  { id: 'count', label: 'Zählt für A/B-Regeln' },
  { id: 'succession', label: 'Nachfolge-Box (Potential Succession Case)' },
  { id: 'knockout', label: 'Ausschluss → Pass (Bench)' },
];
// non-counting criteria never feed the A/B rules
const isKnockout = (c) => c.kind === 'succession' || c.kind === 'knockout';

const slug = (s) => s.toLowerCase().replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 30);

// Live preview — mirrors backend _rule_text()
const ruleText = (criteria, rules) => {
  const counting = criteria.filter(c => !isKnockout(c));
  const labels = Object.fromEntries(counting.map(c => [c.id, c.label || '…']));
  const parts = ['A', 'B'].map(p => {
    const r = rules[p] || {}; const ids = (r.criteria || []).filter(i => labels[i]); const need = Math.min(Number(r.min || 0), ids.length);
    if (!ids.length || need < 1) return `Prio ${p}: nicht vergeben`;
    if (ids.length === counting.length) return need === ids.length ? `Prio ${p}: alle ${ids.length} Kriterien` : `Prio ${p}: mind. ${need} von allen ${ids.length} Kriterien`;
    const names = ids.map(i => labels[i]).join(', ');
    return need === ids.length ? `Prio ${p}: alle von „${names}“` : `Prio ${p}: mind. ${need} von „${names}“`;
  });
  parts.push('darunter Pass (Bench)');
  const succ = criteria.find(c => c.kind === 'succession');
  if (succ) parts.push(`Nachfolge-Box „${succ.label || '…'}“ nicht angekreuzt → A wird C, B wird Pass (Bench)`);
  const ko = criteria.filter(c => c.kind === 'knockout').map(c => c.label || '…');
  if (ko.length) parts.push(`Ausschluss „${ko.join('“, „')}“ angekreuzt → Pass (Bench)`);
  return parts.join(' · ');
};

function CriterionRow({ c, i, total, onChange, onMove, onRemove }) {
  return (
    <div className="border border-border rounded-sm p-3 bg-secondary/20 space-y-2" data-testid={`prio-crit-row-${i}`}>
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-mono text-muted-foreground w-5">{i + 1}.</span>
        <Input value={c.label} onChange={(e) => onChange({ ...c, label: e.target.value })} placeholder="Kurztext an der Checkbox, z. B. „Umsatz > 10 Mio. €“"
          maxLength={120} className="h-8 text-xs bg-background border-border" data-testid={`prio-crit-label-${i}`} />
        <button onClick={() => onMove(i, -1)} disabled={i === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-30" data-testid={`prio-crit-up-${i}`} title="Nach oben"><ArrowUp className="w-3.5 h-3.5" /></button>
        <button onClick={() => onMove(i, 1)} disabled={i === total - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-30" data-testid={`prio-crit-down-${i}`} title="Nach unten"><ArrowDown className="w-3.5 h-3.5" /></button>
        <button onClick={() => onRemove(i)} className="text-muted-foreground hover:text-red-400" data-testid={`prio-crit-remove-${i}`} title="Kriterium entfernen"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
      <Textarea value={c.definition} onChange={(e) => onChange({ ...c, definition: e.target.value })} rows={2} maxLength={600}
        placeholder="Definition (optional) — erscheint beim Hover auf dem „?“ neben der Checkbox"
        className="text-xs bg-background border-border min-h-[44px]" data-testid={`prio-crit-definition-${i}`} />
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-muted-foreground w-5" />
        <select value={c.kind || 'count'} onChange={(e) => onChange({ ...c, kind: e.target.value })} data-testid={`prio-crit-kind-${i}`}
          className={`h-7 text-[11px] rounded-sm border px-2 bg-background ${c.kind === 'knockout' ? 'border-red-500/50 text-red-300' : c.kind === 'succession' ? 'border-emerald-500/50 text-emerald-300' : 'border-border text-foreground'}`}>
          {KINDS.map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
        </select>
        {c.kind === 'knockout' && <span className="text-[10px] text-red-300/80">Angekreuzt → Deal ist ein Pass (Bench)</span>}
        {c.kind === 'succession' && <span className="text-[10px] text-emerald-300/80">Nicht angekreuzt → A-Niveau wird C (kein Nachfolgefall), B-Niveau wird Pass (Bench). Nur eine Nachfolge-Box möglich.</span>}
      </div>
    </div>
  );
}

function RuleEditor({ prio, rule, criteria, onChange }) {
  const selected = rule.criteria || [];
  const toggle = (cid) => onChange({ ...rule, criteria: selected.includes(cid) ? selected.filter(x => x !== cid) : [...selected, cid] });
  const n = selected.length;
  return (
    <div className="border border-border rounded-sm p-3 space-y-2" data-testid={`prio-rule-${prio}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[10px] font-mono font-bold border rounded px-1.5 py-0.5 ${PRIO_STYLES[prio]}`}>Prio {prio}</span>
        <span className="text-xs text-foreground">wenn mindestens</span>
        <Input type="number" min={1} max={Math.max(1, n)} value={rule.min || ''} onChange={(e) => onChange({ ...rule, min: Number(e.target.value) })}
          className="h-7 w-16 text-xs bg-background border-border text-center" data-testid={`prio-rule-${prio}-min`} disabled={n === 0} />
        <span className="text-xs text-foreground">der folgenden {n} Kriterien angekreuzt {n === 1 ? 'ist' : 'sind'}:</span>
        <button onClick={() => onChange({ ...rule, criteria: criteria.map(c => c.id), min: Math.min(rule.min || 1, criteria.length) || 1 })} className="ml-auto text-[10px] text-muted-foreground hover:text-foreground" data-testid={`prio-rule-${prio}-all`}>alle wählen</button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {criteria.map(c => {
          const on = selected.includes(c.id);
          return (
            <button key={c.id} onClick={() => toggle(c.id)} data-testid={`prio-rule-${prio}-crit-${c.id}`}
              className={`text-[11px] px-2 py-1 rounded-sm border transition-colors ${on ? PRIO_STYLES[prio] : 'border-border text-muted-foreground hover:text-foreground hover:bg-secondary/60'}`}>
              {c.label || <span className="italic">ohne Text</span>}
            </button>
          );
        })}
      </div>
      {n === 0 && <p className="text-[10px] text-muted-foreground">Keine Kriterien gewählt — Prio {prio} wird nie vergeben.</p>}
    </div>
  );
}

// Admin (System): texts + "?" definitions for the prio criteria, the A/B rules ("at least N of these") and optional descriptions per prio.
export function PrioConfigCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.getPrioConfig().then(r => setCfg(r.data)).catch(() => toast.error('Prio-Konfiguration konnte nicht geladen werden')); }, []);
  if (!cfg) return null;

  const setCrit = (i, c) => {
    const criteria = cfg.criteria.map((x, j) => (j === i ? c : x));
    let rules = cfg.rules;
    if (isKnockout(c)) {
      rules = Object.fromEntries(['A', 'B'].map(p => {
        const r = cfg.rules[p]; const crit = (r.criteria || []).filter(x => x !== c.id);
        return [p, { criteria: crit, min: Math.min(r.min || 1, crit.length) || (crit.length ? 1 : 0) }];
      }));
    }
    setCfg({ ...cfg, criteria, rules });
  };
  const move = (i, d) => {
    const criteria = [...cfg.criteria]; const [c] = criteria.splice(i, 1); criteria.splice(i + d, 0, c);
    setCfg({ ...cfg, criteria });
  };
  const remove = (i) => {
    const cid = cfg.criteria[i].id;
    const criteria = cfg.criteria.filter((_, j) => j !== i);
    const rules = Object.fromEntries(['A', 'B'].map(p => {
      const r = cfg.rules[p]; const crit = (r.criteria || []).filter(x => x !== cid);
      return [p, { criteria: crit, min: Math.min(r.min || 1, crit.length) || (crit.length ? 1 : 0) }];
    }));
    setCfg({ ...cfg, criteria, rules });
  };
  const add = () => {
    const base = `kriterium_${cfg.criteria.length + 1}`;
    let id = base, k = 1;
    while (cfg.criteria.some(c => c.id === id)) id = `${base}_${k++}`;
    setCfg({ ...cfg, criteria: [...cfg.criteria, { id, label: '', definition: '', kind: 'count' }] });
  };
  const save = async () => {
    setSaving(true);
    try {
      // new criteria get a readable id from their label (ids of existing criteria stay stable — deals reference them)
      const criteria = cfg.criteria.map(c => (c.id.startsWith('kriterium_') && c.label ? { ...c, id: slug(c.label) || c.id } : c));
      const idMap = Object.fromEntries(cfg.criteria.map((c, i) => [c.id, criteria[i].id]));
      const rules = Object.fromEntries(['A', 'B'].map(p => [p, { ...cfg.rules[p], criteria: (cfg.rules[p].criteria || []).map(x => idMap[x] || x) }]));
      const r = await api.setPrioConfig({ criteria, rules, descriptions: cfg.descriptions });
      setCfg(r.data); invalidatePrioConfig();
      toast.success(r.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    setSaving(false);
  };

  return (
    <div className="bg-card border border-border rounded-sm p-4 sm:p-5 space-y-4" data-testid="prio-config-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-heading text-lg text-foreground flex items-center gap-2 mb-1">
            <ListChecks className="text-blue-400" style={{ width: 18, height: 18 }} /> Prio-Kriterien & Regeln
          </h3>
          <p className="text-xs text-muted-foreground max-w-2xl">
            Diese Kriterien kreuzt das Team beim „Deal freigeben“ an. Kurztext steht an der Checkbox, die Definition erscheint beim Hover auf dem „?“.
            Die Regeln legen fest, was für Prio A bzw. B angekreuzt sein muss — darunter ist der Deal ein Pass (Bench). Die Nachfolge-Box entscheidet A ↔ C bzw. B ↔ Pass. Bereits eingestufte Deals behalten ihre Prio.
          </p>
          {cfg.updated_at && <p className="text-[10px] text-muted-foreground/70 mt-1" data-testid="prio-config-updated">Zuletzt geändert {new Date(cfg.updated_at).toLocaleString('de-DE')}{cfg.updated_by ? ` von ${cfg.updated_by}` : ''}</p>}
        </div>
        <Button onClick={save} disabled={saving} className="h-8 bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs" data-testid="prio-config-save">
          {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Speichern
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Kriterien ({cfg.criteria.length})</p>
          {cfg.criteria.map((c, i) => (
            <CriterionRow key={c.id} c={c} i={i} total={cfg.criteria.length} onChange={(nc) => setCrit(i, nc)} onMove={move} onRemove={remove} />
          ))}
          <Button variant="outline" size="sm" onClick={add} disabled={cfg.criteria.length >= 20} className="h-7 text-xs border-border" data-testid="prio-crit-add">
            <Plus className="w-3 h-3 mr-1" /> Kriterium hinzufügen
          </Button>
        </div>
        <div className="space-y-3">
          <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Regeln</p>
          {['A', 'B'].map(p => (
            <RuleEditor key={p} prio={p} rule={cfg.rules[p]} criteria={cfg.criteria.filter(c => !isKnockout(c))} onChange={(r) => setCfg({ ...cfg, rules: { ...cfg.rules, [p]: r } })} />
          ))}
          {cfg.criteria.some(isKnockout) && (
            <p className="text-[10px] text-muted-foreground" data-testid="prio-knockout-note">
              Zählen nicht in den Regeln: {cfg.criteria.filter(isKnockout).map(c => `„${c.label || '…'}“ (${c.kind === 'succession' ? 'Nachfolge-Box' : 'Ausschluss → Pass'})`).join(', ')}
            </p>
          )}
          {cfg.criteria.filter(c => c.kind === 'succession').length > 1 && <p className="text-[10px] text-red-400" data-testid="prio-succession-dup">Nur eine Nachfolge-Box erlaubt.</p>}
          <p className="text-[11px] text-foreground/80 border border-border/60 rounded-sm px-3 py-2 bg-secondary/20" data-testid="prio-config-rule-text">
            So liest es das Team: <span className="text-muted-foreground">{ruleText(cfg.criteria, cfg.rules)}</span>
          </p>
          <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground pt-1">Erklärung je Stufe (optional)</p>
          {['A', 'B', 'C'].map(p => (
            <div key={p} className="flex items-start gap-2">
              <span className={`mt-1.5 text-[10px] font-mono font-bold border rounded px-1.5 py-0.5 ${PRIO_STYLES[p]}`}>Prio {p}</span>
              <Input value={cfg.descriptions?.[p] || ''} onChange={(e) => setCfg({ ...cfg, descriptions: { ...cfg.descriptions, [p]: e.target.value } })} maxLength={300}
                placeholder={p === 'A' ? 'z. B. Top-Deal mit Nachfolgefall — Vier-Augen-Freigabe' : p === 'B' ? 'z. B. Solider Nachfolge-Deal — Standard-Prozess' : 'z. B. A-Qualität, aber (noch) kein Nachfolgefall'}
                className="h-8 text-xs bg-background border-border" data-testid={`prio-desc-${p}`} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
